import { useState } from 'react';

import { Loader2, Send } from 'lucide-react';

import { useGetSandbox } from '@/api/getSandbox';
import { AppTooltip } from '@/components/appTooltip';
import { usePopup } from '@/components/confirmation';
import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import { useCurrentSandbox } from '@/hooks/useCurrentSandbox';
import { useGame } from '@/hooks/useGame';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { toaster } from '@/lib/toaster';
import { Permissions } from '@/models/permission';
import { pendingCountParts, pendingPublish } from '@/models/sandbox';
import { useSandboxContext } from '@/providers';

import { SandboxPublishSummaryDialog } from './sandboxPublishSummaryDialog';
import {
  type PublishSandboxResponse,
  usePublishSandbox,
} from '../api/publishSandbox';

// Mounted only while publishing: a router holds one blocker at a time, and the
// pages this button sits on (imports, translations) may hold their own.
const PublishInProgressGuard = () => {
  useNavigationBlocker(
    true,
    'A publish is in progress. Leaving now hides its result. Leave anyway?',
  );
  return null;
};

/**
 * Publish entry for the current Sandbox, rendered at the end of the breadcrumb trail
 * by the `sandbox` route's `action` handle — so it only ever mounts inside
 * SandboxProvider.
 */
export const SandboxPublishButton = () => {
  const [publishResult, setPublishResult] =
    useState<PublishSandboxResponse | null>(null);

  const { selectedSandbox } = useSandboxContext();
  const game = useGame();
  const { showPopup } = usePopup();

  const { current: currentSandbox } = useCurrentSandbox(
    game.game_code,
    selectedSandbox,
  );

  // GET /v1/sandbox/{id} is gated on sandboxes:read (admin-api router.go), and this
  // button renders on every Sandbox-scoped page — so without the code the query
  // would 403 on every navigation.
  //
  // Known limitation: a Grant carrying sandboxes:publish without sandboxes:read
  // can't reach publish_status at all (the endpoint demands read), so the button
  // stays disabled on "No pending changes" even when there are some. Fixing it
  // needs publish_status on the auth-only sandbox list, not a wider gate here.
  const canReadSandbox = usePermissionCheck({
    oneOf: [Permissions.sandboxes.read],
  });

  const { data: sandboxDetail } = useGetSandbox(
    { sandboxId: currentSandbox?.id ?? '' },
    { enabled: !!currentSandbox?.id && canReadSandbox },
  );

  const pending = pendingPublish(sandboxDetail);

  const publishMutation = usePublishSandbox();

  const handlePublish = async () => {
    if (!currentSandbox || !pending) {
      toaster.info('No sandbox selected');
      return;
    }

    await showPopup({
      title: 'Confirm Publish',
      content: (
        <div className="space-y-2">
          <p>
            Are you sure you want to publish sandbox{' '}
            <strong>{currentSandbox.name}</strong>?
          </p>
          <ul className="text-sm text-muted-foreground">
            This will publish :
            {pendingCountParts(pending).map((part) => (
              <li key={part} className="list-disc list-inside font-bold">
                {part}
              </li>
            ))}
          </ul>
        </div>
      ),
      confirmText: 'Publish',
      cancelText: 'Cancel',
      onConfirm: async () => {
        // publishSandbox already toasts the outcome.
        setPublishResult(
          await publishMutation.mutateAsync({ sandboxId: currentSandbox.id }),
        );
      },
    });
  };

  return (
    <PermissionCheck oneOf={[Permissions.sandboxes.publish]}>
      {publishMutation.isPending && <PublishInProgressGuard />}
      <AppTooltip
        title={
          pending
            ? `Ready to publish: ${pendingCountParts(pending).join(', ')}`
            : 'No pending changes'
        }
      >
        {/* A disabled button fires no pointer events, so the tooltip needs its own
            trigger — and "nothing to publish" is exactly when it is worth reading. */}
        <span>
          <Button
            size="sm"
            variant={pending ? 'default' : 'ghost'}
            disabled={!pending || publishMutation.isPending}
            onClick={() => {
              void handlePublish();
            }}
          >
            {publishMutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Send />
            )}
            Publish
          </Button>
        </span>
      </AppTooltip>
      {publishResult && (
        <SandboxPublishSummaryDialog
          open={!!publishResult}
          onOpenChange={(open) => {
            if (!open) setPublishResult(null);
          }}
          response={publishResult}
        />
      )}
    </PermissionCheck>
  );
};
